/**
 * Utility functions for handling progress calculations with mixed content (lessons + submission forms)
 */

/**
 * Get all content items (lessons + submission forms) from a course
 * @param {Object} course - Course object with lessons, submissionForms arrays, and contentOrder
 * @returns {Array} Array of content items with type and order information
 */
export const getCourseContent = (course) => {
  if (!course) return [];
  
  const content = [];
  
  // If course has contentOrder, use it for proper sequencing
  if (course.contentOrder && course.contentOrder.length > 0) {
    // Create lookup maps for lessons, submission forms, and reinforcements
    const lessonsMap = new Map();
    const formsMap = new Map();
    const reinforcementsMap = new Map();

    // Build lookup maps
    if (course.lessons) {
      course.lessons.forEach(lesson => {
        lessonsMap.set(lesson._id, lesson);
      });
    }

    if (course.submissionForms) {
      course.submissionForms.forEach(form => {
        formsMap.set(form._id, form);
      });
    }

    if (course.reinforcements) {
      course.reinforcements.forEach(reinforcement => {
        reinforcementsMap.set(reinforcement._id, reinforcement);
      });
    }
    
    // Build content array using contentOrder
    course.contentOrder
      .sort((a, b) => a.order - b.order) // Ensure proper order
      .forEach((orderItem, index) => {
        let contentItem = null;

        if (orderItem.type === 'lesson' && lessonsMap.has(orderItem.id)) {
          contentItem = {
            ...lessonsMap.get(orderItem.id),
            type: 'lesson',
            contentType: 'lesson',
            order: index
          };
        } else if (orderItem.type === 'submissionForm' && formsMap.has(orderItem.id)) {
          contentItem = {
            ...formsMap.get(orderItem.id),
            type: 'submissionForm',
            contentType: 'submissionForm',
            order: index
          };
        } else if (orderItem.type === 'reinforcement' && reinforcementsMap.has(orderItem.id)) {
          contentItem = {
            ...reinforcementsMap.get(orderItem.id),
            type: 'reinforcement',
            contentType: 'reinforcement',
            order: index
          };
        }

        if (contentItem) {
          content.push(contentItem);
        }
      });
  } else {
    // Fallback to legacy behavior for backward compatibility
    let currentOrder = 0;

    // Add lessons first
    if (course.lessons && course.lessons.length > 0) {
      course.lessons.forEach((lesson) => {
        content.push({
          ...lesson,
          type: 'lesson',
          contentType: 'lesson',
          order: currentOrder++
        });
      });
    }

    // Add submission forms after lessons
    if (course.submissionForms && course.submissionForms.length > 0) {
      course.submissionForms.forEach((form) => {
        content.push({
          ...form,
          type: 'submissionForm',
          contentType: 'submissionForm',
          order: currentOrder++
        });
      });
    }

    // Add reinforcements after submission forms
    if (course.reinforcements && course.reinforcements.length > 0) {
      course.reinforcements.forEach((reinforcement) => {
        content.push({
          ...reinforcement,
          type: 'reinforcement',
          contentType: 'reinforcement',
          order: currentOrder++
        });
      });
    }
  }
  
  return content;
};

/**
 * Get all content items from a path (all courses)
 * @param {Object} path - Path object with courses array
 * @returns {Array} Array of all content items from all courses
 */
export const getPathContent = (path) => {
  if (!path || !path.courses) return [];
  
  const allContent = [];
  
  path.courses.forEach(course => {
    const courseContent = getCourseContent(course);
    courseContent.forEach(item => {
      allContent.push({
        ...item,
        courseId: course._id,
        courseTitle: course.title
      });
    });
  });
  
  return allContent;
};

/**
 * Calculate course progress including submission forms
 * @param {Object} course - Course object
 * @param {Object} progress - Progress object with completedLessons and completedSubmissionForms
 * @returns {Object} Progress information
 */
export const calculateCourseProgress = (course, progress) => {
  if (!course || !progress) return { percentage: 0, completed: 0, total: 0 };

  const content = getCourseContent(course);

  // EXCLUDE reinforcements from total count - they're bonus content, not required for completion
  const contentWithoutReinforcements = content.filter(item => item.type !== 'reinforcement');
  const totalContent = contentWithoutReinforcements.length;

  if (totalContent === 0) return { percentage: 0, completed: 0, total: 0 };

  let completedContent = 0;

  // Count completed lessons
  if (progress.completedLessons) {
    const completedLessonIds = progress.completedLessons.map(cl =>
      typeof cl.lesson === 'object' ? cl.lesson._id : cl.lesson
    );

    contentWithoutReinforcements.forEach(item => {
      if (item.type === 'lesson' && completedLessonIds.includes(item._id)) {
        completedContent++;
      }
    });
  }

  // Count completed submission forms
  if (progress.completedSubmissionForms) {
    const completedFormIds = progress.completedSubmissionForms.map(cf =>
      typeof cf.submissionForm === 'object' ? cf.submissionForm._id : cf.submissionForm
    );

    contentWithoutReinforcements.forEach(item => {
      if (item.type === 'submissionForm' && completedFormIds.includes(item._id)) {
        completedContent++;
      }
    });
  }

  // Reinforcements are NOT counted toward progress percentage
  // They are bonus content and don't affect course completion

  const percentage = Math.round((completedContent / totalContent) * 100);

  return {
    percentage,
    completed: completedContent,
    total: totalContent,
    lessons: contentWithoutReinforcements.filter(item => item.type === 'lesson').length,
    submissionForms: contentWithoutReinforcements.filter(item => item.type === 'submissionForm').length,
    reinforcements: 0  // Not counted in progress
  };
};

/**
 * Check if a content item is completed
 * @param {Object} contentItem - Content item (lesson or submission form)
 * @param {Object} progress - Progress object
 * @returns {Boolean} True if completed
 */
export const isContentCompleted = (contentItem, progress) => {
  if (!contentItem || !progress) return false;

  if (contentItem.type === 'lesson' && progress.completedLessons) {
    const completedLessonIds = progress.completedLessons.map(cl =>
      typeof cl.lesson === 'object' ? cl.lesson._id : cl.lesson
    );
    return completedLessonIds.includes(contentItem._id);
  }

  if (contentItem.type === 'submissionForm' && progress.completedSubmissionForms) {
    const completedFormIds = progress.completedSubmissionForms.map(cf =>
      typeof cf.submissionForm === 'object' ? cf.submissionForm._id : cf.submissionForm
    );
    return completedFormIds.includes(contentItem._id);
  }

  if (contentItem.type === 'reinforcement' && progress.completedReinforcements) {
    const completedReinforcementIds = progress.completedReinforcements.map(cr =>
      typeof cr.reinforcement === 'object' ? cr.reinforcement._id : cr.reinforcement
    );
    return completedReinforcementIds.includes(contentItem._id);
  }

  return false;
};

/**
 * Get the current content item from progress
 * @param {Object} progress - Progress object
 * @param {Object} path - Path object
 * @returns {Object|null} Current content item or null
 */
export const getCurrentContent = (progress, path) => {
  if (!progress || !progress.currentLesson || !path) return null;
  
  const allContent = getPathContent(path);
  return allContent.find(content => content._id === progress.currentLesson) || null;
};

/**
 * Get the next content item
 * @param {Object} progress - Progress object
 * @param {Object} path - Path object
 * @returns {Object|null} Next content item or null
 */
export const getNextContent = (progress, path) => {
  if (!progress || !path) return null;
  
  const allContent = getPathContent(path);
  
  if (!progress.currentLesson) {
    return allContent[0] || null;
  }
  
  const currentIndex = allContent.findIndex(content => content._id === progress.currentLesson);
  if (currentIndex === -1 || currentIndex === allContent.length - 1) {
    return null; // No next content
  }
  
  return allContent[currentIndex + 1];
};

/**
 * Check if a course is completed
 * @param {Object} course - Course object
 * @param {Object} progress - Progress object
 * @returns {Boolean} True if course is completed
 */
export const isCourseCompleted = (course, progress) => {
  const courseProgress = calculateCourseProgress(course, progress);
  return courseProgress.completed === courseProgress.total && courseProgress.total > 0;
};

/**
 * Get content type label
 * @param {String} contentType - Content type ('lesson' or 'submissionForm')
 * @returns {String} Human-readable label
 */
export const getContentTypeLabel = (contentType) => {
  switch (contentType) {
    case 'lesson':
      return 'Lesson';
    case 'submissionForm':
      return 'Assignment';
    case 'reinforcement':
      return 'Reinforcement';
    default:
      return 'Content';
  }
};

/**
 * Get content type icon
 * @param {String} contentType - Content type ('lesson' or 'submissionForm')
 * @returns {String} Icon name
 */
export const getContentTypeIcon = (contentType) => {
  switch (contentType) {
    case 'lesson':
      return 'FaBookOpen';
    case 'submissionForm':
      return 'FaClipboardList';
    case 'reinforcement':
      return 'FaDumbbell';
    default:
      return 'FaFile';
  }
};

/**
 * Get previous content item for back navigation
 * @param {Number} currentIndex - Current content index
 * @param {Array} courseContent - Array of course content
 * @returns {Object|null} Previous content item or null
 */
export const getPreviousContent = (currentIndex, courseContent) => {
  if (!courseContent || currentIndex <= 0) return null;
  return courseContent[currentIndex - 1] || null;
};

/**
 * Get next content item for forward navigation by index
 * @param {Number} currentIndex - Current content index
 * @param {Array} courseContent - Array of course content
 * @returns {Object|null} Next content item or null
 */
export const getNextContentByIndex = (currentIndex, courseContent) => {
  if (!courseContent || currentIndex >= courseContent.length - 1) return null;
  return courseContent[currentIndex + 1] || null;
};

/**
 * Check if user can navigate back from current position
 * @param {Number} currentIndex - Current content index
 * @returns {Boolean} True if can navigate back
 */
export const canNavigateBack = (currentIndex) => {
  return currentIndex > 0;
};

/**
 * Check if user can navigate forward while browsing backwards
 * @param {Number} viewingIndex - Currently viewing index
 * @param {Number} actualProgressIndex - User's actual progress index
 * @returns {Boolean} True if can navigate forward
 */
export const canNavigateForward = (viewingIndex, actualProgressIndex) => {
  return viewingIndex < actualProgressIndex;
};

/**
 * Validate navigation state
 * @param {Object} navigationState - Navigation state object
 * @param {Array} courseContent - Array of course content
 * @param {Number} actualProgressIndex - User's actual progress index
 * @returns {Boolean} True if navigation state is valid
 */
export const validateNavigationState = (navigationState, courseContent, actualProgressIndex) => {
  if (!navigationState || !courseContent) return false;
  
  const { viewingIndex, isViewingBackwards } = navigationState;
  
  // Check bounds
  if (viewingIndex < 0 || viewingIndex >= courseContent.length) return false;
  
  // If viewing backwards, ensure we're not ahead of actual progress
  if (isViewingBackwards && viewingIndex >= actualProgressIndex) return false;
  
  return true;
};

// Cross-course navigation utilities

/**
 * Get all content across all courses in proper order
 * @param {Array} pathCourses - Array of courses from user's path
 * @returns {Array} Flattened array of all content with course metadata
 */
export const getAllPathContent = (pathCourses) => {
  if (!pathCourses || !Array.isArray(pathCourses)) return [];
  
  const allContent = [];
  
  pathCourses.forEach(course => {
    const courseContent = getCourseContent(course);
    courseContent.forEach(content => {
      allContent.push({
        ...content,
        courseId: course._id,
        courseTitle: course.title
      });
    });
  });
  
  return allContent;
};

/**
 * Find global content index across all courses
 * @param {String} contentId - Content ID to find
 * @param {Array} pathCourses - Array of courses from user's path
 * @returns {Number} Global index or -1 if not found
 */
export const getGlobalContentIndex = (contentId, pathCourses) => {
  if (!contentId || !pathCourses) return -1;
  
  const allContent = getAllPathContent(pathCourses);
  return allContent.findIndex(content => content._id === contentId);
};

/**
 * Get previous content across course boundaries
 * @param {Number} currentGlobalIndex - Current global index
 * @param {Array} allPathContent - All content across all courses
 * @returns {Object|null} Previous content or null
 */
export const getPreviousContentGlobal = (currentGlobalIndex, allPathContent) => {
  if (!allPathContent || currentGlobalIndex <= 0) return null;
  return allPathContent[currentGlobalIndex - 1] || null;
};

/**
 * Get next content across course boundaries
 * @param {Number} currentGlobalIndex - Current global index  
 * @param {Array} allPathContent - All content across all courses
 * @returns {Object|null} Next content or null
 */
export const getNextContentGlobal = (currentGlobalIndex, allPathContent) => {
  if (!allPathContent || currentGlobalIndex >= allPathContent.length - 1) return null;
  return allPathContent[currentGlobalIndex + 1] || null;
};

/**
 * Check if content is navigable (completed or current)
 * @param {Object} content - Content item with navigation metadata
 * @param {Object} userProgress - User progress data
 * @returns {Boolean} True if navigable
 */
export const isContentNavigable = (content, userProgress) => {
  if (!content || !userProgress) return false;
  
  // If content has canNavigate flag from backend, use it
  if (content.hasOwnProperty('canNavigate')) {
    return content.canNavigate;
  }
  
  // Fallback to checking completion status
  const isCompleted = isContentCompleted(content, userProgress);
  const isCurrent = userProgress.currentContent === content._id;
  
  return isCompleted || isCurrent;
};

/**
 * Get course containing specific content
 * @param {String} contentId - Content ID
 * @param {Array} pathCourses - Array of courses from user's path
 * @returns {Object|null} Course object or null
 */
export const getCourseForContent = (contentId, pathCourses) => {
  if (!contentId || !pathCourses) return null;
  
  for (const course of pathCourses) {
    const courseContent = getCourseContent(course);
    const hasContent = courseContent.some(content => content._id === contentId);
    if (hasContent) {
      return course;
    }
  }
  
  return null;
};

/**
 * Validate global navigation bounds
 * @param {Number} globalIndex - Global index to validate
 * @param {Array} allPathContent - All content across all courses
 * @param {Object} userProgress - User progress data
 * @returns {Boolean} True if valid navigation
 */
export const validateGlobalNavigation = (globalIndex, allPathContent, userProgress) => {
  if (!allPathContent || globalIndex < 0 || globalIndex >= allPathContent.length) {
    return false;
  }
  
  const content = allPathContent[globalIndex];
  return isContentNavigable(content, userProgress);
}; 